"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.EvidenceLinkObject = exports.EVIDENCE_LINK_TOPICS = exports.DataRowAddressSchema = void 0;
exports.evidenceLinkTopicFor = evidenceLinkTopicFor;
const z = require("zod");
const boundary_conditions_comparison_1 = require("../Consensus/comparisons/boundary-conditions.comparison");
const signal_attributes_comparison_1 = require("../Consensus/comparisons/signal-attributes.comparison");
const consensus_types_1 = require("../Consensus/consensus.types");
const slice_key_1 = require("../Consensus/slice-key");
const evidence_topic_1 = require("./evidence-topic");
const evidence_errors_1 = require("./evidence.errors");
exports.DataRowAddressSchema = z.object({
    packageId: z.string().min(1),
    sliceKey: slice_key_1.SliceKeySchema,
    sliceVersion: z.number().int().min(1),
    rowKey: z.string(),
    lineageId: z.string().min(1).optional(),
});
exports.EVIDENCE_LINK_TOPICS = {
    [slice_key_1.SliceKey.AmrBoundaries]: evidence_topic_1.EvidenceTopic.Amr,
    [slice_key_1.SliceKey.OcBoundaries]: evidence_topic_1.EvidenceTopic.OperatingConditions,
    [slice_key_1.SliceKey.SignalAttributes]: evidence_topic_1.EvidenceTopic.PinDescriptions,
};
function evidenceLinkTopicFor(sliceKey) {
    const topic = exports.EVIDENCE_LINK_TOPICS[sliceKey];
    if (topic === undefined) {
        throw new evidence_errors_1.EvidenceLinkSliceNotSupportedError(sliceKey);
    }
    return topic;
}
class EvidenceLinkObject {
    static { this.SEPARATOR = '\x1F'; }
    static { this.POSITIVE_INTEGER = /^[1-9]\d*$/; }
    static { this.BOUNDARY_SHAPE = {
        rowKeySegments: new boundary_conditions_comparison_1.BoundaryConditionsComparison().alignment.keyColumns.length,
        takesLineage: true,
    }; }
    static { this.ROW_SHAPES = {
        [slice_key_1.SliceKey.AmrBoundaries]: EvidenceLinkObject.BOUNDARY_SHAPE,
        [slice_key_1.SliceKey.OcBoundaries]: EvidenceLinkObject.BOUNDARY_SHAPE,
        [slice_key_1.SliceKey.SignalAttributes]: {
            rowKeySegments: new signal_attributes_comparison_1.SignalAttributesComparison().alignment.keyColumns.length,
            takesLineage: false,
        },
    }; }
    static forRow(address) {
        const { packageId, sliceKey, sliceVersion, rowKey, lineageId } = address;
        const shape = EvidenceLinkObject.shapeOf(sliceKey);
        const sep = EvidenceLinkObject.SEPARATOR;
        const rowObject = [packageId, sliceKey, String(sliceVersion), consensus_types_1.AnchorKind.Row, rowKey].join(sep);
        const object = lineageId === undefined ? rowObject : `${rowObject}${sep}${lineageId}`;
        if (packageId === '' || packageId.includes(sep)) {
            throw new evidence_errors_1.InvalidEvidenceLinkObjectError(object, 'the package id must be non-empty and free of the separator');
        }
        if (!EvidenceLinkObject.POSITIVE_INTEGER.test(String(sliceVersion))) {
            throw new evidence_errors_1.InvalidEvidenceLinkObjectError(object, `'${sliceVersion}' is not a slice version`);
        }
        if (rowKey.split(sep).length !== shape.rowKeySegments) {
            throw new evidence_errors_1.InvalidEvidenceLinkObjectError(object, `a ${sliceKey} row key has ${shape.rowKeySegments} components`);
        }
        if (lineageId !== undefined) {
            if (!shape.takesLineage) {
                throw new evidence_errors_1.InvalidEvidenceLinkObjectError(object, `${sliceKey} links name no parent row`);
            }
            if (lineageId === '' || lineageId.includes(sep)) {
                throw new evidence_errors_1.InvalidEvidenceLinkObjectError(object, 'the parent row id must be non-empty and free of the separator');
            }
        }
        return object;
    }
    static parse(object) {
        const [packageId, sliceToken, versionToken, kind, ...rest] = object.split(EvidenceLinkObject.SEPARATOR);
        if (packageId === undefined || packageId === '') {
            throw new evidence_errors_1.InvalidEvidenceLinkObjectError(object, 'expected a package id');
        }
        const sliceKey = slice_key_1.SliceKeySchema.safeParse(sliceToken);
        if (!sliceKey.success) {
            throw new evidence_errors_1.InvalidEvidenceLinkObjectError(object, `'${sliceToken ?? ''}' is not a slice key`);
        }
        const shape = EvidenceLinkObject.ROW_SHAPES[sliceKey.data];
        if (shape === undefined) {
            throw new evidence_errors_1.InvalidEvidenceLinkObjectError(object, `${sliceKey.data} rows take no links`);
        }
        if (versionToken === undefined || !EvidenceLinkObject.POSITIVE_INTEGER.test(versionToken)) {
            throw new evidence_errors_1.InvalidEvidenceLinkObjectError(object, `'${versionToken ?? ''}' is not a slice version`);
        }
        if (kind !== String(consensus_types_1.AnchorKind.Row)) {
            throw new evidence_errors_1.InvalidEvidenceLinkObjectError(object, 'expected a row anchor');
        }
        const row = {
            packageId,
            sliceKey: sliceKey.data,
            sliceVersion: Number(versionToken),
            rowKey: rest.slice(0, shape.rowKeySegments).join(EvidenceLinkObject.SEPARATOR),
        };
        if (rest.length === shape.rowKeySegments) {
            return row;
        }
        const lineageId = rest.at(-1);
        if (rest.length === shape.rowKeySegments + 1 && shape.takesLineage && lineageId !== undefined && lineageId !== '') {
            return { ...row, lineageId };
        }
        throw new evidence_errors_1.InvalidEvidenceLinkObjectError(object, `expected a ${sliceKey.data} row key of ${shape.rowKeySegments} components` +
            (shape.takesLineage ? ', optionally followed by a parent row id' : ''));
    }
    static slicePrefix(packageId, sliceKey) {
        return [packageId, sliceKey, ''].join(EvidenceLinkObject.SEPARATOR);
    }
    static rowPrefix(packageId, sliceKey, sliceVersion, rowKey) {
        return `${EvidenceLinkObject.forRow({ packageId, sliceKey, sliceVersion, rowKey })}${EvidenceLinkObject.SEPARATOR}`;
    }
    static shapeOf(sliceKey) {
        const shape = EvidenceLinkObject.ROW_SHAPES[sliceKey];
        if (shape === undefined) {
            throw new evidence_errors_1.EvidenceLinkSliceNotSupportedError(sliceKey);
        }
        return shape;
    }
}
exports.EvidenceLinkObject = EvidenceLinkObject;
//# sourceMappingURL=evidence-link.js.map